import * as React from 'react';
import cx from 'classnames';
import invariant from 'invariant';

import { MouseEventHandler } from 'types/typescript/eventHandlers';

import './styles/FloatingBox.scss';

const BODY_PADDING = 25;

type Coordinates = { x: number; y: number };
type Position = 'bottom-right' | 'top-right' | 'top-left' | 'bottom-left';

/* Calculate the initial coordinates of the box. */
function initialCoordinates(width: number, height: number, position: Position): Coordinates {
  // future ssr (?)
  if (document && document.body) {
    switch (position) {
      case 'top-right':
        return {
          x: document.body.offsetWidth - (width + BODY_PADDING),
          y: BODY_PADDING,
        };
      case 'bottom-right':
        return {
          x: document.body.offsetWidth - (width + BODY_PADDING),
          y: document.body.offsetHeight - (height + BODY_PADDING),
        };
      case 'bottom-left':
        return {
          x: BODY_PADDING,
          y: document.body.offsetHeight - (height + BODY_PADDING),
        };
      case 'top-left':
        return {
          x: BODY_PADDING,
          y: BODY_PADDING,
        };
      default:
        return {
          x: BODY_PADDING,
          y: BODY_PADDING,
        };
    }
  } else {
    return {
      x: BODY_PADDING,
      y: BODY_PADDING,
    };
  }
}

type Props = {
  // The width of the children component
  // we need this to position the element correctly in the top right corner.
  // we cant compute this from the children because then we would have to mount it first.
  width: number;
  height: number;
  position: Position;
  children?: React.ReactNode;
  suppressTextSelection: boolean;
};

type State = {
  innerX: number;
  innerY: number;
  pursuing: boolean;
} & Coordinates;

class FloatingBox extends React.Component<Props, State> {
  state: State = {
    // X coordinate of the click inside the floating box.
    innerX: 0,
    // Y coordinate of the click inside the floating box.
    innerY: 0,
    // True when box is in pursuit of the cursor.
    pursuing: false,
    // Coordinates of the floating box.
    ...initialCoordinates(this.props.width, this.props.height || 0, this.props.position),
  };

  static defaultProps = {
    position: 'top-right',
  };

  componentDidMount() {
    // when the window is resized we fake a mouse move to the
    // current coordinates of the floating box, this will cause
    // the max boundary to be check and the floating box keep in view.
    window.addEventListener('resize', this.fakeMouseMove);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.fakeMouseMove);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  }

  box: any;

  fakeMouseMove = () => {
    this.handleMouseMove(
      {
        clientX: this.box.offsetLeft + this.state.innerX,
        clientY: this.box.offsetTop + this.state.innerY,
      },
      { resize: true }
    );
  };

  handleMouseDown: MouseEventHandler = (event) => {
    // this prevent text from being selected (like it does when
    // you click and drag your cursor over a text)
    if (this.props.suppressTextSelection) {
      event.preventDefault();
    }

    // calculate the click coordinates inside of the floating box, we calculate
    // this because it needs to be taken into account when moving the box around
    // otherwise the cursor will always snap to the top left corner of the floating box.
    this.setState({
      pursuing: true,
      innerX: event.pageX - event.currentTarget.offsetLeft,
      innerY: event.pageY - event.currentTarget.offsetTop,
    });
  };

  handleMouseUp = () => {
    this.setState({ pursuing: false });
  };

  handleMouseMove = (
    event: {
      clientX: number;
      clientY: number;
    },
    options: { resize?: boolean } = {}
  ) => {
    if (!this.state.pursuing && !options.resize) {
      return;
    }

    invariant(document.body, 'cannot compute without a document body');

    let x = event.clientX - this.state.innerX;
    let y = event.clientY - this.state.innerY;

    // x & y can never be smaller than the padding.
    x = x < BODY_PADDING ? BODY_PADDING : x;
    y = y < BODY_PADDING ? BODY_PADDING : y;

    const maxX = document.body.offsetWidth - (this.box.offsetWidth + BODY_PADDING);
    const maxY = document.body.offsetHeight - (this.box.offsetHeight + BODY_PADDING);

    // x & y can never be larger than the size of window - (the size the box + the padding)
    x = x > maxX ? maxX : x;
    y = y > maxY ? maxY : y;

    this.setState({ x, y });
  };

  render() {
    const styles = { top: this.state.y, left: this.state.x };
    const classes = cx('FloatingBox', {
      'FloatingBox--pursuing': this.state.pursuing,
    });

    return (
      <div
        style={styles}
        className={classes}
        ref={(b) => (this.box = b)}
        onMouseDown={this.handleMouseDown}
      >
        {this.props.children}
      </div>
    );
  }
}

export default FloatingBox;
